/**
 * Invoice Error Handling Utilities
 * Provides consistent error handling and normalization for invoice-related operations
 */

import { toast } from "react-toastify";

/**
 * Normalizes error objects to a consistent format
 * @param {Error|string|object} error - The error to normalize
 * @param {string} defaultMessage - Default message if error is invalid
 * @returns {object} Normalized error object with { message, type, originalError, status }
 */
export const normalizeError = (error, defaultMessage = 'An unexpected error occurred') => {
  if (!error) {
    return {
      message: defaultMessage,
      type: 'unknown',
      originalError: null,
      status: null,
    };
  }

  // If it's already a normalized error object, return it
  if (error && typeof error === 'object' && error.message && error.type) {
    return error;
  }

  // Handle axios errors (most common)
  if (error && typeof error === 'object' && error.response) {
    const status = error.response.status;
    const responseData = error.response.data || {};
    
    // Extract message from various possible locations in the response
    let message = responseData.message || 
                  responseData.error?.message || 
                  responseData.details?.detail || 
                  responseData.details?.title || 
                  error.message || 
                  defaultMessage;
    
    let type = 'error';
    if (status === 401) {
      type = 'unauthorized';
    } else if (status === 403) {
      type = 'forbidden';
    } else if (status === 404) {
      type = 'notFound';
    } else if (status === 408) {
      type = 'timeout';
    } else if (status === 429) {
      type = 'rateLimit';
    } else if (status >= 500) {
      type = 'server';
    } else if (status >= 400) {
      type = 'validation';
    }

    return {
      message,
      type,
      originalError: error,
      status,
    };
  }

  // Handle network errors (no response)
  if (error && typeof error === 'object' && error.request && !error.response) {
    return {
      message: 'Network error. Please check your connection.',
      type: 'network',
      originalError: error,
      status: null,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      type: 'error',
      originalError: error,
      status: null,
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    return {
      message: error.message || defaultMessage,
      type: 'error',
      originalError: error,
      status: null,
    };
  }

  // Fallback for unknown error types
  return {
    message: defaultMessage,
    type: 'unknown',
    originalError: error,
    status: null,
  };
};

/**
 * Gets user-friendly error message based on error type
 * @param {object} normalizedError - Normalized error object
 * @param {string} context - Context where error occurred (e.g., "fetching invoices", "exporting data")
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (normalizedError, context = '') => {
  const { type, message, status, originalError } = normalizedError;
  const contextText = context ? ` while ${context}` : '';

  // Try to extract more specific error details from the response
  let specificMessage = message;
  if (originalError?.response?.data) {
    const errorData = originalError.response.data;
    
    // Handle error structure with error field and details
    if (errorData.error && errorData.details) {
      const errorCode = errorData.error;
      const details = errorData.details;
      
      // Map error codes to user-friendly messages
      if (errorCode === 'ERROR_BAD_REQUEST') {
        // Extract specific validation errors if available
        if (details.detail && details.detail !== 'Bad Request') {
          specificMessage = details.detail;
        } else if (details.title && details.title !== 'Bad request.') {
          specificMessage = details.title;
        } else {
          // Provide context-specific messages based on operation
          if (context.includes('creating') || context.includes('create')) {
            specificMessage = 'Unable to create invoice. Please check that all required fields are filled correctly and try again.';
          } else if (context.includes('updating') || context.includes('update')) {
            specificMessage = 'Unable to update invoice. Please verify your changes and try again.';
          } else if (context.includes('deleting') || context.includes('delete')) {
            specificMessage = 'Unable to delete invoice. The invoice may be in use or you may not have permission.';
          } else {
            specificMessage = 'Invalid request. Please check your input and try again.';
          }
        }
      }
    }
    
    // Handle other error formats
    if (errorData.message && !specificMessage) {
      specificMessage = errorData.message;
    }
  }

  switch (type) {
    case 'unauthorized':
      return 'Your session has expired. Please refresh the page to sign in again.';
    case 'forbidden':
      return 'You don\'t have permission to perform this action. Please contact your administrator if you believe this is an error.';
    case 'notFound':
      return 'The requested invoice or resource was not found. It may have been deleted or moved.';
    case 'timeout':
      return 'The request took too long to complete. Please check your connection and try again.';
    case 'rateLimit':
      return 'Too many requests. Please wait a moment before trying again.';
    case 'server':
      return 'A server error occurred. Please try again in a few moments. If the problem continues, contact support.';
    case 'network':
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    case 'validation':
      return specificMessage || 'Please check that all required fields are filled correctly and try again.';
    case 'deleted':
      return 'Your account has been deactivated. Please contact support for assistance.';
    default:
      return specificMessage || `An unexpected error occurred${contextText}. Please try again or contact support if the problem persists.`;
  }
};

/**
 * Checks if an error is retryable
 * @param {object} normalizedError - Normalized error object
 * @returns {boolean} True if error is retryable
 */
export const isRetryableError = (normalizedError) => {
  const { type, status } = normalizedError;
  
  // Retryable errors: network, timeout, server errors (5xx), rate limit
  return (
    type === 'network' ||
    type === 'timeout' ||
    type === 'server' ||
    type === 'rateLimit' ||
    (status && status >= 500)
  );
};

/**
 * Centralized error handler for invoice operations
 * Logs error, sends to Slack, and displays user-friendly toast notification
 * 
 * @param {Error|string|object} error - The error to handle
 * @param {string} context - Context where error occurred (e.g., "fetching invoices", "exporting data")
 * @param {object} options - Additional options
 * @param {boolean} options.showToast - Whether to show toast notification (default: true)
 * @param {boolean} options.sendToSlack - Whether to send to Slack (default: true)
 * @param {boolean} options.logToConsole - Whether to log to console (default: true)
 * @param {string} options.customMessage - Custom error message to display
 * @returns {object} Normalized error object
 */
export const handleInvoiceError = (
  error,
  context = '',
  options = {}
) => {
  const {
    showToast = true,
    sendToSlack = true,
    logToConsole = true,
    customMessage = null,
  } = options;

  const normalizedError = normalizeError(error);
  const errorMessage = customMessage || getErrorMessage(normalizedError, context);

  // Log to console
  if (logToConsole) {
    console.error(`Error${context ? ` in ${context}` : ''}:`, normalizedError.originalError || error);
  }

  // Send to Slack
  if (sendToSlack && global?.config?.slackMessage) {
    try {
      global.config.slackMessage(error.toString());
    } catch (slackError) {
      console.error('Failed to send error to Slack:', slackError);
    }
  }

  // Show toast notification
  if (showToast) {
    toast.error(errorMessage, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }

  return normalizedError;
};

/**
 * Handles user deletion error specifically
 * This is a special case that requires logout and navigation
 * 
 * @param {Function} handleUserDeletion - Function to handle user deletion (logout, navigate, etc.)
 * @returns {void}
 */
export const handleUserDeletionError = (handleUserDeletion) => {
  if (handleUserDeletion && typeof handleUserDeletion === 'function') {
    handleUserDeletion();
  } else {
    // Fallback if handleUserDeletion is not provided
    toast.error("This user has been deleted!", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  }
};

/**
 * Handles export errors specifically
 * 
 * @param {Error|string|object} error - The error to handle
 * @param {string} operation - Operation name (e.g., "exporting to Excel", "downloading PDFs")
 * @returns {void}
 */
export const handleExportError = (error, operation = 'exporting data') => {
  handleInvoiceError(error, operation, {
    customMessage: `Error occurred while ${operation}.`,
  });
};

/**
 * Handles download errors specifically
 * 
 * @param {Error|string|object} error - The error to handle
 * @param {string} operation - Operation name (e.g., "downloading invoices", "creating PDFs")
 * @returns {void}
 */
export const handleDownloadError = (error, operation = 'downloading files') => {
  handleInvoiceError(error, operation, {
    customMessage: `Something went wrong while ${operation}.`,
  });
};

